import * as React from 'react';

import classNames from 'classnames';
import _ from 'underscore';

import {colors} from '../../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon as Clear} from '../../icon/svg/close-icon';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconSearchIcon} from '../../icon/svg/sidebar-icon-search';
import {TextField} from '../../textfield/textfield';

import styles from './search-bar.module.css';

const DEFAULT_HEIGHT = '35px';
const ACTIVE_BORDER_COLOR = '#1ba0e1'; // This is `var(--blue)`

type Props = {
    onChange: (arg1: string) => any;
    value?: string;
    placeholder?: string;
    autoFocus?: boolean;
    onBlur?: () => any;
    onFocus?: () => any;
    searchBarRef?: (arg1?: HTMLInputElement | null | undefined) => any;
    hasBorder?: boolean;
    styleOptions?: {
        height?: string | number;
        borderColor?: string;
    };
    // Renders with less padding
    isSmall?: boolean;
    // This is the accessibility label string
    hiddenLabel?: string;
    isDark?: boolean;
    isClearable?: boolean;
};

export function SearchBar(props: Props) {
    const {value = '', placeholder = 'Search…'} = props;
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        if (props.autoFocus) {
            _.defer(() => {
                if (inputRef.current) {
                    // @ts-expect-error
                    inputRef.current.focus();
                }
            });
        }
    }, [props.autoFocus]);

    const handleChange = (newValue: string) => {
        props.onChange(newValue);
    };

    const handleClear = () => {
        props.onChange('');
    };

    const handleFocus = () => {
        setIsFocused(true);
        props.onFocus?.();
    };

    const handleBlur = () => {
        setIsFocused(false);
        props.onBlur?.();
    };

    const handleRefAssignment = (node?: HTMLInputElement | null) => {
        // @ts-expect-error
        inputRef.current = node;
        props.searchBarRef?.(node);
    };

    const containerStyle = {
        height:
            props.styleOptions && props.styleOptions.height
                ? props.styleOptions.height
                : DEFAULT_HEIGHT,
        borderColor: isFocused && props.hasBorder ? ACTIVE_BORDER_COLOR : undefined,
    } as const;

    const styleName = classNames({
        [styles.bordered]: props.hasBorder,
        [styles['no-border']]: !props.hasBorder,
        [styles['bordered--dark']]: props.hasBorder && props.isDark,
        [styles['no-border--dark']]: !props.hasBorder && props.isDark,
    });

    return (
        <div style={containerStyle} className={styleName}>
            <div
                className={classNames(styles['search-container'], {
                    [styles['search-container--small']]: props.isSmall,
                    [styles['search-container--dark']]: props.isDark,
                })}
            >
                <SidebarIconSearchIcon
                    wrapWithDiv={false}
                    size={15}
                    color={props.isDark ? colors.white : colors.tagGrey}
                />
            </div>
            <div
                className={classNames(styles.input, {
                    [styles['input--small']]: props.isSmall,
                    [styles['input--dark']]: props.isDark,
                })}
            >
                {props.hiddenLabel ? (
                    <label htmlFor='search' className='display-none'>
                        {props.hiddenLabel}
                    </label>
                ) : undefined}
                <TextField
                    id='search'
                    value={value}
                    placeholder={placeholder}
                    textFieldRef={handleRefAssignment}
                    noBorder={true}
                    onFocus={handleFocus}
                    // @ts-expect-error
                    onBlur={handleBlur}
                    onChange={handleChange}
                />
                {value && props.isClearable ? (
                    <button
                        onClick={handleClear}
                        className={props.isSmall ? styles['clear--small'] : styles.clear}
                    >
                        <Clear
                            size={10}
                            className={props.isSmall ? styles['clear--small'] : styles.clear}
                        />
                    </button>
                ) : undefined}
            </div>
        </div>
    );
}
